import type { MetaFunction } from 'react-router'
import { Link, useLoaderData } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { ArrowLeft, ArrowRight, BookOpen, Clock } from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

const CAT_ORDER: Array<'cumul' | 'fraude' | 'operationnel' | 'reglementation'> = [
  'cumul',
  'operationnel',
  'fraude',
  'reglementation',
]

export async function loader() {
  const { CEE_GUIDES, GUIDE_CATEGORY_LABELS } = await import('~/lib/cee-guides-data.server')
  // Slim list — index cards only need slug/title/description/readingMinutes.
  // Heavy intro/sections/keyPoints bodies stay server-side.
  const byCategory = CAT_ORDER.map((cat) => ({
    cat,
    label: GUIDE_CATEGORY_LABELS[cat],
    guides: CEE_GUIDES
      .filter((g) => g.category === cat)
      .map((g) => ({
        slug: g.slug,
        title: g.title,
        description: g.description,
        readingMinutes: g.readingMinutes,
      })),
  }))
  return { byCategory }
}

export const meta: MetaFunction = () => [
  { title: "Guides CEE et MaPrimeRénov' — 10 dossiers détaillés | AfficheServices" },
  { name: 'description', content: "10 guides détaillés sur les primes CEE, MaPrimeRénov', le cumul des aides, la fraude RGE et la rénovation en copropriété. Contenu actualisé 2026." },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/cee/guides' },
]

export default function CEEGuidesIndexPage() {
  const { byCategory } = useLoaderData<typeof loader>()

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'CEE', url: 'https://www.afficheservices.fr/cee' },
        { name: 'Guides', url: 'https://www.afficheservices.fr/cee/guides' },
      ]} />

      <section className="bg-gradient-to-br from-indigo-900 via-blue-900 to-slate-900 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl">
          <Link to="/cee" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-200 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Retour au catalogue CEE
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-blue-200">
            <BookOpen className="h-3.5 w-3.5" /> 10 guides détaillés
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-4xl">
            Guides CEE &amp; MaPrimeRénov&apos;
          </h1>
          <p className="mt-3 max-w-2xl text-base text-blue-200">
            Les règles techniques, les pièges et les combinaisons d&apos;aides expliquées sans jargon.
            Contenu actualisé pour 2026.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 py-12 lg:px-6">
        {byCategory.map(({ cat, label, guides }) => (
          <section key={cat} className="mt-8 first:mt-0">
            <h2 className="text-lg font-bold text-slate-900">{label}</h2>
            <div className="mt-4 space-y-3">
              {guides.map((g) => (
                <Link
                  key={g.slug}
                  to={`/cee/guides/${g.slug}`}
                  className="group flex items-start justify-between gap-4 rounded-xl border border-border bg-white p-5 shadow-xs transition-all hover:border-primary hover:shadow-md"
                >
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-bold text-slate-900 group-hover:text-primary">{g.title}</h3>
                    <p className="mt-1 text-sm text-slate-500 line-clamp-2">{g.description}</p>
                    <div className="mt-2 flex items-center gap-1 text-xs text-slate-400">
                      <Clock className="h-3 w-3" /> {g.readingMinutes} min de lecture
                    </div>
                  </div>
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-300 group-hover:text-primary" />
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>

      <Footer />
    </div>
  )
}
